import { Button, ButtonProps, Center, Container, Flex } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { Icon, IconName } from '../../atoms';

type PaginationProps = {
  pageCount: number;
  currentPage: number;
  onPage: (pageNumber: number) => void;
};

function PaginationButton({
  isCurrent = false,
  ...props
}: ButtonProps & { isCurrent?: boolean }): JSX.Element {
  return (
    <Button
      border="none"
      alignItems="center"
      justifyContent="center"
      borderRadius="revert"
      w="2rem"
      h="2rem"
      transition="ease-in all 250ms"
      color={isCurrent ? 'white' : 'inherit'}
      backgroundColor={isCurrent ? 'palette_blue.100' : 'palette_gray.5'}
      _hover={{
        cursor: 'pointer',
        backgroundColor: 'palette_blue.100',
        color: 'palette_white'
      }}
      _disabled={{
        color: 'inherit',
        backgroundColor: 'palette_gray.5',
        _hover: {
          backgroundColor: 'palette_blue.100',
          color: 'palette_white'
        }
      }}
      variant="unstyled"
      {...props}
    />
  );
}

function Pagination({
  pageCount,
  currentPage,
  onPage
}: PaginationProps): JSX.Element {
  const buttons = [];

  const constructButton = (index?: number): ReactNode => (
    <PaginationButton
      isDisabled={index === undefined}
      key={`random-${Math.random()}`}
      isCurrent={currentPage === index}
      onClick={(): void => onPage(index ?? 1)}
    >
      {index ?? '...'}
    </PaginationButton>
  );

  for (let i = 1; i <= pageCount; i++) {
    buttons.push(constructButton(i));
  }

  return (
    <Container>
      <Center>
        <Flex
          sx={{
            '&>button:first-of-type': {
              borderTopLeftRadius: '50%',
              borderBottomLeftRadius: '50%'
            },
            '&>button:last-of-type': {
              borderTopRightRadius: '50%',
              borderBottomRightRadius: '50%'
            }
          }}
        >
          <PaginationButton
            data-testid="previous-page"
            role="navigation"
            onClick={(): void =>
              onPage(currentPage - 1 <= 0 ? 1 : currentPage - 1)
            }
          >
            <Icon w="0.7rem" h="0.7rem" name={IconName.ArrowLeft} />
          </PaginationButton>
          {pageCount > 5 &&
            currentPage <= 3 &&
            [1, 2, 3, undefined, pageCount - 1, pageCount].map((index) =>
              constructButton(index)
            )}
          {pageCount > 5 &&
            currentPage > 3 &&
            currentPage < pageCount - 2 &&
            [
              1,
              currentPage - 1,
              currentPage,
              currentPage + 1,
              undefined,
              pageCount
            ].map((i) => constructButton(i))}
          {pageCount > 5 &&
            currentPage >= pageCount - 2 &&
            [1, undefined, pageCount - 2, pageCount - 1, pageCount].map((i) =>
              constructButton(i)
            )}
          {pageCount <= 5 && buttons}
          <PaginationButton
            data-testid="next-page"
            role="navigation"
            onClick={(): void =>
              onPage(currentPage + 1 > pageCount ? pageCount : currentPage + 1)
            }
          >
            <Icon w="0.7rem" h="0.7rem" name={IconName.ArrowRight} />
          </PaginationButton>
        </Flex>
      </Center>
    </Container>
  );
}

export { Pagination };
